import type { OnboardingSection } from "@qendo/onboarded";
import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useState } from "react";
import { Onboarded } from "~/entities/onboarded";
import { useSession } from "~/providers/SessionProvider";
import { OnboardingKey, onboardingStorage } from "~/util/secure-storage";

export type OnboardingState = {
    visible(section: OnboardingSection): boolean;
    dismiss(section: OnboardingSection): void;
    show(section: OnboardingSection): void;
    reset(): void;
};

const OnboardingContext = createContext<OnboardingState>({
    visible: (_section) => false,
    dismiss: (_section) => {
        return;
    },
    show: (_section) => {
        return;
    },
    reset: () => {
        return;
    },
});

export function useOnboarding() {
    const value = useContext(OnboardingContext);
    if (!value) {
        throw new Error("useOnboarding must be wrapped in a <OnboardingProvider />");
    }

    return value;
}

export function OnboardingProvider({ children }: PropsWithChildren) {
    const [mounted, setMounted] = useState(false);
    const [onboarded, setOnboarded] = useState<Onboarded>(new Onboarded());
    const { is_share_user } = useSession();

    useEffect(() => {
        onboardingStorage
            .getItem(OnboardingKey.onboarded)
            .then((value) => setOnboarded(Onboarded.from(value ?? {})))
            .finally(() => setMounted(true));
    }, []);

    const visible = useCallback(
        (section: OnboardingSection) => mounted && !is_share_user && !onboarded[section],
        [mounted, onboarded, is_share_user],
    );
    const dismiss = (section: OnboardingSection) => {
        if (visible(section)) {
            const updated = Onboarded.from({ ...onboarded, [section]: true });
            onboardingStorage.setItem(OnboardingKey.onboarded, updated);
            setOnboarded(updated);
        }
    };
    const show = (section: OnboardingSection) => {
        if (!visible(section)) {
            const updated = Onboarded.from({ ...onboarded, [section]: false });
            onboardingStorage.setItem(OnboardingKey.onboarded, updated);
            setOnboarded(updated);
        }
    };

    const reset = () => {
        const updated = new Onboarded();
        onboardingStorage.setItem(OnboardingKey.onboarded, updated);
        setOnboarded(updated);
    };

    const value = {
        visible,
        dismiss,
        show,
        reset,
    };

    return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}
